import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import NotificationCenter from '../../components/ui/NotificationCenter';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SystemStatusIndicator from '../../components/ui/SystemStatusIndicator';

// Import all components
import CompanyProfileSection from './components/CompanyProfileSection';
import BiddingCriteriaSection from './components/BiddingCriteriaSection';
import GeographicTargetingSection from './components/GeographicTargetingSection';
import OperationalHoursSection from './components/OperationalHoursSection';
import PaymentSettingsSection from './components/PaymentSettingsSection';
import IntegrationStatusPanel from './components/IntegrationStatusPanel';

const BuyerRegistrationProfileManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSystemStatusOpen, setIsSystemStatusOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Mock data for buyer profile
  const [buyerProfile, setBuyerProfile] = useState({
    companyName: "Premium Insurance Solutions",
    companyType: "insurance_agency",
    licenseNumber: "INS-2024-TX-8847",
    licensedStates: ["TX", "CA", "FL", "NY"],
    primaryContactName: "Sarah Johnson",
    primaryContactEmail: "sarah.johnson@premiumins.com",
    phoneNumber: "(555) 123-4567",
    website: "https://www.premiumins.com",
    businessAddress: "1234 Insurance Blvd",
    city: "Dallas",
    state: "TX",
    zipCode: "75201",
    companyLogo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=200&h=200&fit=crop&crop=center"
  });

  const [biddingCriteria, setBiddingCriteria] = useState({
    insuranceTypes: ["auto", "home", "life"],
    trafficSources: ["search", "social", "direct_mail"],
    ageRange: { min: 25, max: 65 },
    maxBidAmount: 45.00,
    autoBidIncrement: 0.25,
    maxAutoBids: 4,
    expectedDailyLeads: 25,
    excludeDuplicates: true,
    realtimeVerification: true,
    weekendBidding: false,
    holidayBidding: false
  });

  const [geographicTargeting, setGeographicTargeting] = useState({
    targetStates: ["TX", "CA", "FL"],
    zipCodes: ["75201", "75202", "75203", "90210", "33101"],
    serviceRadius: 50,
    urbanOnly: false,
    excludeRural: true,
    includeSuburban: true
  });

  const [operationalHours, setOperationalHours] = useState({
    primaryTimezone: "America/Chicago",
    acceptAllTimezones: false,
    weeklySchedule: {
      monday: { enabled: true, startTime: "08:00", endTime: "18:00", breakStart: "12:00", breakEnd: "13:00" },
      tuesday: { enabled: true, startTime: "08:00", endTime: "18:00", breakStart: "12:00", breakEnd: "13:00" },
      wednesday: { enabled: true, startTime: "08:00", endTime: "18:00", breakStart: "12:00", breakEnd: "13:00" },
      thursday: { enabled: true, startTime: "08:00", endTime: "18:00", breakStart: "12:00", breakEnd: "13:00" },
      friday: { enabled: true, startTime: "08:00", endTime: "18:00", breakStart: "12:00", breakEnd: "13:00" },
      saturday: { enabled: false, startTime: "", endTime: "", breakStart: "", breakEnd: "" },
      sunday: { enabled: false, startTime: "", endTime: "", breakStart: "", breakEnd: "" }
    },
    holidaySchedule: {
      newYear: false,
      martinLutherKing: true,
      presidents: true,
      memorial: false,
      independence: false,
      labor: true,
      columbus: true,
      veterans: true,
      thanksgiving: false,
      christmas: false
    },
    alwaysAvailable: false,
    weekendOperations: false,
    afterHoursEmergency: true,
    autoPauseDuringBreaks: true
  });

  const [paymentSettings, setPaymentSettings] = useState({
    primaryPaymentMethod: "credit_card",
    cardDetails: {
      holderName: "Sarah Johnson",
      type: "visa",
      number: "4532123456789012",
      expiry: "12/26",
      cvv: "123"
    },
    bankDetails: {
      accountHolderName: "",
      bankName: "",
      routingNumber: "",
      accountNumber: ""
    },
    billingCycle: "weekly",
    spendingLimit: 5000,
    lowBalanceAlert: 500,
    autoReloadAmount: 1000,
    autoReload: true,
    emailNotifications: true,
    smsAlerts: true,
    detailedReports: true
  });

  const [integrationStatus, setIntegrationStatus] = useState([]);

  const tabs = [
    { id: 'profile', label: 'Company Profile', icon: 'Building2' },
    { id: 'bidding', label: 'Bidding Criteria', icon: 'Target' },
    { id: 'geographic', label: 'Geographic Targeting', icon: 'MapPin' },
    { id: 'hours', label: 'Operational Hours', icon: 'Clock' },
    { id: 'payment', label: 'Payment Settings', icon: 'CreditCard' },
    { id: 'integrations', label: 'Integrations', icon: 'Zap' }
  ];

  useEffect(() => {
    // Keyboard navigation for tabs
    const handleKeyDown = (event) => {
      if (event?.ctrlKey && event?.key >= '1' && event?.key <= '6') {
        event?.preventDefault();
        const tabIndex = parseInt(event?.key) - 1;
        if (tabs?.[tabIndex]) {
          setActiveTab(tabs?.[tabIndex]?.id);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSectionUpdate = (section, data) => {
    switch (section) {
      case 'profile':
        setBuyerProfile(data);
        break;
      case 'bidding':
        setBiddingCriteria(data);
        break;
      case 'geographic':
        setGeographicTargeting(data);
        break;
      case 'hours':
        setOperationalHours(data);
        break;
      case 'payment':
        setPaymentSettings(data);
        break;
      default:
        break;
    }
    setHasUnsavedChanges(false);
  };

  const handleToggleEdit = (section) => {
    setEditingSection(editingSection === section ? null : section);
    setHasUnsavedChanges(true);
  };

  const handleQuickAction = (actionId, selectedItems) => {
    switch (actionId) {
      case 'update_profile': setActiveTab('profile');
        setEditingSection('profile');
        break;
      case 'bulk_bid': navigate('/buyer-bid-management-interface');
        break;
      case 'set_auto_bid': setActiveTab('bidding');
        setEditingSection('bidding');
        break;
      case 'export_leads':
        // Handle export functionality
        console.log('Exporting leads...');
        break;
      default:
        console.log('Quick action:', actionId, selectedItems);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <CompanyProfileSection
            profile={buyerProfile}
            onUpdate={(data) => handleSectionUpdate('profile', data)}
            isEditing={editingSection === 'profile'}
            onToggleEdit={() => handleToggleEdit('profile')}
          />
        );
      case 'bidding':
        return (
          <BiddingCriteriaSection
            criteria={biddingCriteria}
            onUpdate={(data) => handleSectionUpdate('bidding', data)}
            isEditing={editingSection === 'bidding'}
            onToggleEdit={() => handleToggleEdit('bidding')}
          />
        );
      case 'geographic':
        return (
          <GeographicTargetingSection
            targeting={geographicTargeting}
            onUpdate={(data) => handleSectionUpdate('geographic', data)}
            isEditing={editingSection === 'geographic'}
            onToggleEdit={() => handleToggleEdit('geographic')}
          />
        );
      case 'hours':
        return (
          <OperationalHoursSection
            hours={operationalHours}
            onUpdate={(data) => handleSectionUpdate('hours', data)}
            isEditing={editingSection === 'hours'}
            onToggleEdit={() => handleToggleEdit('hours')}
          />
        );
      case 'payment':
        return (
          <PaymentSettingsSection
            settings={paymentSettings}
            onUpdate={(data) => handleSectionUpdate('payment', data)}
            isEditing={editingSection === 'payment'}
            onToggleEdit={() => handleToggleEdit('payment')}
          />
        );
      case 'integrations':
        return (
          <IntegrationStatusPanel
            integrations={integrationStatus}
            onRefresh={setIntegrationStatus}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole="buyer"
        isNotificationOpen={isNotificationOpen}
        onNotificationToggle={() => setIsNotificationOpen(!isNotificationOpen)}
      />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Users" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Buyer Registration & Profile Management</h1>
                <p className="text-muted-foreground">Configure your bidding criteria and operational parameters</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {hasUnsavedChanges && (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-warning/10 text-warning rounded-lg">
                  <Icon name="AlertTriangle" size={16} />
                  <span className="text-sm">Unsaved changes</span>
                </div>
              )}
              <Button variant="outline" iconName="Download">
                Export Profile
              </Button>
              <Button variant="default" iconName="Save">
                Save All Changes
              </Button>
            </div>
          </div>

          {/* Quick Action Toolbar */}
          <div className="mb-6">
            <QuickActionToolbar
              section="buyer"
              selectedItems={[]}
              onAction={handleQuickAction}
            />
          </div>

          {/* Tab Navigation */}
          <div className="bg-card border border-border rounded-lg mb-6">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab, index) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-smooth ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  title={`Switch to ${tab?.label} (Ctrl+${index + 1})`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                  {editingSection === tab?.id && (
                    <div className="w-2 h-2 bg-warning rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
      {/* Notification Center */}
      <NotificationCenter
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      />
      {/* System Status Indicator */}
      <SystemStatusIndicator
        isExpanded={isSystemStatusOpen}
        onToggle={() => setIsSystemStatusOpen(!isSystemStatusOpen)}
      />
    </div>
  );
};

export default BuyerRegistrationProfileManagement;